import store from "@/store";
import httpClient from "@/http/httpClient";
import { toast } from "@starline-security/component-library";

const status = {
  ON_HOLD: "On Hold",
  PENDING_APPROVAL: "Pending Approval",
};

export default {
  namespaced: true,
  state: {
    dealers: [],
    allDealers: [],
    sortType: "alphabetical",
    sortDirection: "asc",
    searchText: "",
  },
  getters: {},
  mutations: {
    setDealers(state, payload) {
      state.allDealers = payload;
      state.dealers = state.allDealers;
    },
    setSortType(state, { sortType, sortDirection }) {
      state.sortType = sortType;
      state.sortDirection = sortDirection || "asc";
    },
    setSearchText(state, searchText) {
      state.searchText = searchText;
    },
    markCancel(state, { dealerId, order }) {
      let data = state.allDealers;
      let dealer = data.find((d) => d.id === dealerId);
      if (dealer) {
        dealer.orders = dealer.orders.filter((o) => o.number !== order.number);
        if (!dealer.orders.length) {
          data = data.filter((d) => d.id !== dealerId);
        }
      }
      state.allDealers = data;
      state.dealers = data;
    },
    markHold(state, { dealerId, orderIds }) {
      let data = state.allDealers;
      let dealer = data.find((d) => d.id === dealerId);
      if (dealer) {
        const affectedOrders = dealer.orders.filter((o) =>
          orderIds.includes(o.number)
        );
        affectedOrders.forEach((order) => {
          order.status = status.ON_HOLD;
        });
      }
      state.allDealers = [...data];
      state.dealers = [...data];
    },
    markApproved(state, { dealerId, orderIds }) {
      let data = state.allDealers;
      data.forEach((dealer) => {
        dealer.orders
          .filter((x) => orderIds.includes(x.number))
          .forEach((x) => (x.isApproved = true));
      });
      state.allDealers = data;
      state.dealers = data;
    },
  },
  actions: {
    async fetchAll({ commit }, filters = {}) {
      store.commit("preloader/preloader", true);
      const sortOptions = {
        newest: { field: "date", direction: "desc" },
        oldest: { field: "date", direction: "asc" },
        alphabetical: { field: "name", direction: "asc" },
      };

      try {
        const sortKey = filters.sortType || "alphabetical";
        const sortData = sortOptions[sortKey] || sortOptions.alphabetical;

        const params = new URLSearchParams();

        params.append("order_by", sortData.field);
        params.append("order_direction", sortData.direction);

        if (filters.searchText) {
          params.append("search", filters.searchText);
        }

        const url = params.toString()
          ? `financial-control/get?${params.toString()}`
          : "financial-control/get";

        const response = await httpClient.get(url);

        commit("setDealers", response.data.data);
        commit("setSortType", {
          sortType: sortKey,
          sortDirection: sortData.direction,
        });
      } catch (error) {
        store.commit(
          "errorsModal/openModal",
          error.response?.data?.message || error.response?.data?.errors
        );
      } finally {
        store.commit("preloader/preloader", false);
      }
    },

    updateSort({ commit }, payload) {
      commit("setSortType", payload);
    },
    updateSearch({ commit }, searchText) {
      commit("setSearchText", searchText);
    },

    async cancelOne({ commit }, { dealerId, order }) {
      try {
        store.commit("preloader/preloader", true);
        await httpClient.post("financial-control/cancel", {
          order_ids: [order.number],
        });

        commit("markCancel", { dealerId, order });
        toast.success("Order canceled");
        return true;
      } catch (error) {
        console.error("Error on Cancel: ", error);
        store.commit("errorsModal/openModal", error.response?.data?.errors);
        return false;
      } finally {
        store.commit("preloader/preloader", false);
      }
    },

    async hold({ commit }, { dealerId, orders }) {
      try {
        store.commit("preloader/preloader", true);
        const orderIds = orders.map((o) => o.number);
        await httpClient.post("financial-control/hold", {
          order_ids: orderIds,
        });

        commit("markHold", { dealerId, orderIds });
        toast.success(
          orders.length > 1 ? "Orders put on hold" : "Order put on hold"
        );

        return true;
      } catch (error) {
        store.commit("errorsModal/openModal", error.response?.data?.errors);
        return false;
      } finally {
        store.commit("preloader/preloader", false);
      }
    },

    async approve({ commit }, { dealerId, orders }) {
      try {
        store.commit("preloader/preloader", true);
        const orderIds = orders.map((o) => o.number);
        await httpClient.post("financial-control/approve", {
          orders: orders,
        });

        const message =
          orders.length > 1 ? "Orders approved" : "Order approved";
        toast.success(message);
        commit("markApproved", { dealerId, orderIds });
        return true;
      } catch (error) {
        store.commit("errorsModal/openModal", error.response?.data?.errors);
        return false;
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
  },
};
